import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import { useValidateInvitationAndSetPassword } from 'src/api/user';

const PasswordSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { validateInvitationAndSetPassword } = useValidateInvitationAndSetPassword();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== passwordConfirmation) {
      setError("Passwords don't match");
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const invitationToken = searchParams.get('invitation_token');

    if (!invitationToken) {
      setError('No invitation token provided');
      return;
    }

    try {
      const response = await validateInvitationAndSetPassword(invitationToken, password, passwordConfirmation);
      if (response.status === 'success') {
        navigate('/dashboard', { replace: true });
      } else {
        setError(response.message || 'An error occurred');
      }
    } catch (err) {
      setError('An error occurred while setting up your account');
      console.error('Error setting up account:', err);
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
  };

  return (
    <>
      <Helmet>
        <title>Set Your Password</title>
      </Helmet>
      <Container maxWidth="sm">
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Set Your Password
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Confirm Password"
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
            >
              Set Password
            </Button>
          </form>
        </Box>
      </Container>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PasswordSetupPage;
